import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Loader2 } from "lucide-react";

async function getCroppedBlob(src, cropPixels, outSize = 512) {
  const image = await new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
  const canvas = document.createElement("canvas");
  canvas.width = outSize;
  canvas.height = outSize;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, outSize, outSize);
  ctx.drawImage(image, cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height, 0, 0, outSize, outSize);
  return new Promise((res) => canvas.toBlob((b) => res(b), "image/jpeg", 0.9));
}

export default function ImageCropper({ file, open, onCancel, onCropped, title = "Crop image" }) {
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (!file) { setSrc(null); return; }
    const url = URL.createObjectURL(file);
    setSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onComplete = useCallback((_, px) => setPixels(px), []);

  const confirm = async () => {
    if (!src || !pixels) return;
    setBusy(true);
    const blob = await getCroppedBlob(src, pixels);
    setBusy(false);
    onCropped(blob);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="cropper-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif">{title}</DialogTitle>
          <DialogDescription>Drag to reposition and use the slider to zoom. Images are saved as a square.</DialogDescription>
        </DialogHeader>
        <div className="relative w-full h-64 bg-[#2C3531] rounded-xl overflow-hidden">
          {src && (
            <Cropper image={src} crop={crop} zoom={zoom} aspect={1} cropShape="rect" showGrid
              onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />
          )}
        </div>
        <div className="px-1">
          <Slider value={[zoom]} min={1} max={3} step={0.05} onValueChange={(v) => setZoom(v[0])} data-testid="cropper-zoom" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="rounded-full">Cancel</Button>
          <Button onClick={confirm} disabled={busy} data-testid="cropper-confirm" className="rounded-full bg-[#2C3531] hover:bg-[#1e2422]">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crop & upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
